import { describeError } from '@/lib/form/errorMessages';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { errorKeyFor } from '@/features/catalog/errorMessages';
import { ConfirmDialog } from '@/features/users/components/ConfirmDialog';

/**
 * Danger zone shared by every catalog edit form.
 *
 * Retiring an entry succeeds even while projects reference it — those line
 * items keep their own copy of every value — so the confirmation step is the
 * only thing standing between a misclick and an item disappearing from the
 * catalog.
 */
export function CatalogDangerZone(props: {
  itemName: string;
  pending: boolean;
  error: unknown;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4">
      <h2 className="text-sm font-semibold text-destructive">{t('catalog.danger.title')}</h2>
      <p className="mt-1 text-xs text-destructive">{t('catalog.danger.description')}</p>
      {props.error != null && (
        <div className="mt-2 text-xs text-destructive">
          {describeError(t, props.error, errorKeyFor)}
        </div>
      )}
      <Button
        type="button"
        variant="destructive"
        className="mt-3"
        disabled={props.pending}
        onClick={() => setConfirmOpen(true)}
      >
        {t('catalog.danger.deleteButton')}
      </Button>

      <ConfirmDialog
        open={confirmOpen}
        title={t('catalog.danger.confirmTitle')}
        description={t('catalog.danger.confirmDescription', { name: props.itemName })}
        confirmLabel={t('catalog.danger.deleteButton')}
        variant="destructive"
        loading={props.pending}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          props.onConfirm();
        }}
      />
    </div>
  );
}
